import { Link } from 'react-router-dom';
import { imageUrl } from '../../api/client';
import { timeAgo } from '../../utils/timeAgo';
import type { SetSummary } from '../../api/setsTypes';

interface SetCardProps {
  set: SetSummary;
  // Hub-vyn kan vilja visa flera micro-thumbs om backend levererar dem.
  // För nu använder vi bara coverImageId — micro-thumbs skulle kräva extra
  // backend-fält (collageImageIds liknande shoots).
  microThumbIds?: string[];
}

const MAX_VISIBLE_TAGS = 3;

// SetCard — kompakt kort för Hub-sidans Sets-kolumn.
// Layout: 120x120 cover (eller dark gradient med ⊞ glyph om tom) + body med
// namn, description (1-2 rader), tag-chips, frames-count + relativ aktivitet.
export function SetCard({ set, microThumbIds = [] }: SetCardProps) {
  const accent = set.color ?? '#5fa8d3';
  const hasCover = !!set.coverImageId;
  const visibleTags = set.tags.slice(0, MAX_VISIBLE_TAGS);
  const overflowTagCount = Math.max(0, set.tags.length - MAX_VISIBLE_TAGS);
  const overflowFrames = Math.max(0, set.imageCount - 3);

  // Backend ger bara coverImageId — micro-thumbs är optional och tomma än så
  // länge. Vi visar bara stor cover om vi inte har thumbs, vilket är OK.
  const showMicros = microThumbIds.length >= 2;

  return (
    <Link
      to={`/sets/${set.id}`}
      className="group block text-loupe-text no-underline transition-all duration-300 p-3.5 bg-loupe-bg-2 border border-loupe-border rounded-2xl hover:-translate-y-0.5 hover:border-loupe-border-2 hover:bg-loupe-bg-3"
      style={{ ['--set-accent' as string]: accent }}
    >
      <div className="grid grid-cols-[120px_1fr] gap-3.5 items-stretch">
        {/* Cover */}
        <div
          className="relative w-[120px] h-[120px] rounded-[10px] overflow-hidden bg-loupe-bg-3 grid"
          style={{
            gridTemplateColumns: showMicros ? '2fr 1fr' : '1fr',
            gap: showMicros ? 3 : 0,
          }}
        >
          {hasCover ? (
            <>
              <div className={showMicros ? 'row-span-2 overflow-hidden bg-loupe-bg-3' : 'overflow-hidden bg-loupe-bg-3'}>
                <img
                  src={imageUrl(set.coverImageId!, 'preview')}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                />
              </div>
              {showMicros ? (
                <>
                  <div className="overflow-hidden bg-loupe-bg-3">
                    <img
                      src={imageUrl(microThumbIds[0], 'thumb')}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    />
                  </div>
                  <div className="overflow-hidden bg-loupe-bg-3 relative">
                    <img
                      src={imageUrl(microThumbIds[1], 'thumb')}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    />
                    {overflowFrames > 0 ? (
                      <div className="absolute inset-0 bg-black/55 flex items-center justify-center font-mono text-[11px] text-white/95 tracking-wider">
                        +{overflowFrames}
                      </div>
                    ) : null}
                  </div>
                </>
              ) : null}
            </>
          ) : (
            // Empty state: ⊞-glyph på dark gradient med set:s accent-färg
            <EmptyCover accent={accent} />
          )}
        </div>

        {/* Body */}
        <div className="flex flex-col justify-between min-w-0">
          <div>
            <div className="font-display text-[17px] font-medium tracking-tight leading-[1.25] mb-1 flex items-center gap-1.5 text-loupe-text">
              <span
                className="w-[3px] h-3.5 rounded-sm flex-shrink-0"
                style={{ background: accent }}
              />
              <span className="truncate">{set.name}</span>
            </div>
            {set.description ? (
              <div
                className="text-xs text-loupe-text-2 leading-snug mb-2.5 overflow-hidden"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {set.description}
              </div>
            ) : null}
            {visibleTags.length > 0 ? (
              <div className="flex flex-wrap gap-1 mb-2">
                {visibleTags.map((t) => (
                  <span
                    key={t}
                    className="text-[10px] px-1.5 py-0.5 bg-loupe-bg-3 text-loupe-text-2 border border-loupe-border rounded-full"
                  >
                    {t}
                  </span>
                ))}
                {overflowTagCount > 0 ? (
                  <span className="text-[10px] px-1.5 py-0.5 bg-transparent text-loupe-text-3 border border-loupe-border rounded-full">
                    +{overflowTagCount}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-2.5 text-[10px] text-loupe-text-3 tracking-wider uppercase tabular-nums">
            <span>
              <strong className="text-loupe-text-2 font-medium">
                {set.imageCount}
              </strong>{' '}
              frames
              {set.tags.length > 0 ? (
                <>
                  <span className="opacity-40 mx-1">·</span>
                  {set.tags.length} tag{set.tags.length === 1 ? '' : 's'}
                </>
              ) : null}
            </span>
            <span className="text-loupe-text-2 normal-case tracking-tight text-[11px]">
              {timeAgo(set.lastActivityAt)}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// Tom cover — accent-färgad gradient + Fraunces ⊞-glyph.
function EmptyCover({ accent }: { accent: string }) {
  return (
    <div
      className="w-full h-full flex items-center justify-center"
      style={{
        background: `linear-gradient(135deg, ${accent} 0%, transparent 60%), #1a1a1a`,
      }}
    >
      <span
        className="font-display text-3xl select-none"
        style={{ color: 'rgba(255,255,255,0.25)' }}
      >
        ⊞
      </span>
    </div>
  );
}
